import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, X } from 'lucide-react';
import { ajouterAdresse, PHOTOS_MAX } from './requetesLogique';
import { lirePhoto } from './requetesApi';
import { styleChamp, styleEtiquette } from './FenetreRequete';

/** Photos (3 au maximum), prises avec l'appareil photo du cellulaire ou choisies. */
export const ChoixPhotos = ({ photos, onChange }) => {
  const { t } = useTranslation();
  const entree = useRef(null);
  const ajouter = async (e) => {
    const fichiers = Array.from(e.target.files || []).slice(0, PHOTOS_MAX - photos.length);
    e.target.value = '';
    const lues = [];
    for (const f of fichiers) {
      try { lues.push(await lirePhoto(f)); } catch (err) { /* photo illisible ignorée */ }
    }
    onChange([...photos, ...lues].slice(0, PHOTOS_MAX));
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        {photos.map((p, i) => (
          <div key={i} style={{ position: 'relative' }}>
            <img src={p} alt="" style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }} />
            <button
              type="button" aria-label={t('requetes.retirerPhoto')}
              onClick={() => onChange(photos.filter((_, j) => j !== i))}
              style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {photos.length < PHOTOS_MAX && (
          <button
            type="button" onClick={() => entree.current && entree.current.click()}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '8px', border: '1px dashed #94a3b8', background: '#f8fafc', cursor: 'pointer', color: '#334155', fontSize: '0.85rem' }}
          >
            <Camera size={16} /> {t('requetes.ajouterPhotos', { max: PHOTOS_MAX })}
          </button>
        )}
      </div>
      <input ref={entree} type="file" accept="image/*" multiple onChange={ajouter} style={{ display: 'none' }} />
    </div>
  );
};

/**
 * Destinataires : utilisateurs (cases à cocher, l'auteur toujours coché) + adresses externes
 * (travaux publics, carnet d'adresses). Liste vide d'utilisateurs (droit de lecture absent) :
 * on affiche seulement le nombre de destinataires internes.
 */
export const ChoixDestinataires = ({ users, contacts, ids, emails, auteurId, onChange, aide }) => {
  const { t } = useTranslation();
  const [saisie, setSaisie] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const actifs = (users || []).filter((u) => (u.statut || 'Actif') === 'Actif');
  const basculer = (id) => {
    if (id === auteurId) return;
    onChange(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id], emails);
  };
  const ajouter = (adresse) => {
    onChange(ids, ajouterAdresse(emails, adresse));
    setSaisie('');
  };
  const nomContact = (email) => (contacts || []).find((c) => String(c.email || '').toLowerCase() === email)?.nom;
  return (
    <div>
      <span style={styleEtiquette}>{t('requetes.destinataires')}</span>
      <p style={{ margin: '0 0 8px', fontSize: '0.8rem', color: '#64748b' }}>{aide || t('requetes.destinatairesAide')}</p>
      {actifs.length > 0 ? (
        <div>
          <button type="button" onClick={() => setOuvert(!ouvert)}
            style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '0.85rem', fontWeight: 600 }}>
            {t('requetes.personnelCoche', { nb: ids.length })} {ouvert ? '▲' : '▼'}
          </button>
          {ouvert && (
            <div style={{ maxHeight: '180px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 10px', marginTop: '6px' }}>
              {actifs.map((u) => (
                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', fontSize: '0.88rem' }}>
                  <input type="checkbox" checked={ids.includes(u.id)} disabled={u.id === auteurId} onChange={() => basculer(u.id)} />
                  {u.nom}, {u.prenom}{u.id === auteurId ? ` (${t('requetes.auteur')})` : ''}
                </label>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#334155' }}>{t('requetes.personnelCoche', { nb: ids.length })}</p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '8px 0' }}>
        {emails.map((e) => (
          <span key={e} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#eff6ff', color: '#1e40af', borderRadius: '12px', padding: '3px 10px', fontSize: '0.8rem' }}>
            {nomContact(e) ? `${nomContact(e)} — ${e}` : e}
            <button type="button" aria-label={t('requetes.retirer')} onClick={() => onChange(ids, emails.filter((x) => x !== e))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1e40af', padding: 0, display: 'flex' }}>
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          list="requetes-contacts" type="email" value={saisie} placeholder={t('requetes.ajouterAdresse')}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouter(saisie); } }}
          style={{ ...styleChamp, flex: 1 }}
        />
        <button type="button" onClick={() => ajouter(saisie)}
          style={{ padding: '0 14px', borderRadius: '6px', border: '1px solid #2563eb', background: 'white', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}>
          {t('requetes.ajouter')}
        </button>
        <datalist id="requetes-contacts">
          {(contacts || []).map((c) => <option key={c.id || c.email} value={c.email}>{c.nom}</option>)}
        </datalist>
      </div>
    </div>
  );
};
